import { useSwapContext } from "@src/contexts";
import { PendingTransactionView } from "../PendingTransactionView";
import { SuccessView } from "../SuccessView";
import { ErrorView } from "../ErrorView";
import {
  Button,
  ButtonSizes,
  Navigation,
  PageLayout,
  Skeleton,
  Spinner,
  TokenLogo,
} from "@src/components";
import {
  DepositAndBuyStatus,
  ProcessingState,
  RoutePath,
  SwapMode,
} from "@src/interfaces";
import { ChainlinkSwapFailedView } from "../ChainlinkSwapFailedView";
import { CloseIcon } from "@src/assets";
import { useNavigate } from "react-router-dom";
import { DepositAndBuySteps } from "@src/features";
import { useMemo } from "react";

export const ProcessingView = () => {
  const navigate = useNavigate();

  const {
    swapMode,
    token,
    displayToken,
    cashToken,
    tokenOutAmount,
    estimatedTokenOutAmount,
    shift4TokenAmount,
    cashAmount,
    tokenAmount,
    isUsingChainlinkFunction,
    transactionHash,
    transactionState,
    cashbackAmount,
    pointsAmount,
    transactionError,
    isFetchingRoute,
    executeSwapTransaction,
    chain,
    isDepositAndBuyFlow,
    fetchAllBalances,
    resetSwapFormData,
  } = useSwapContext();

  const navTitle = useMemo(() => {
    if (!isDepositAndBuyFlow) {
      return (
        <p className="text-xl">
          {swapMode === SwapMode.Buy ? "Buy" : "Sell"} {token?.symbol}
        </p>
      );
    }

    switch (transactionState) {
      case ProcessingState.DepositSuccess:
        return (
          <DepositAndBuySteps status={DepositAndBuyStatus.DepositSuccess} />
        );
      case ProcessingState.Pending:
        return <DepositAndBuySteps status={DepositAndBuyStatus.SwapPending} />;
      case ProcessingState.TransactionSuccess:
        return <DepositAndBuySteps status={DepositAndBuyStatus.SwapSuccess} />;
    }
  }, [transactionState, isDepositAndBuyFlow, swapMode, token?.symbol]);

  return (
    <PageLayout
      navigation={
        <Navigation
          leftItems={[
            {
              content: navTitle,
              isCustomContent: true,
            },
          ]}
          rightItems={[
            {
              content: <CloseIcon />,
              onClick: () => {
                fetchAllBalances();
                resetSwapFormData();

                swapMode === SwapMode.Buy
                  ? navigate(RoutePath.Buy)
                  : navigate(RoutePath.Sell);
              },
            },
          ]}
        />
      }
    >
      <>
        {transactionState === ProcessingState.Pending && (
          <PendingTransactionView
            type={isUsingChainlinkFunction ? "advancedSwap" : "simpleSwap"}
            cashAmount={cashAmount}
            fromAmount={
              swapMode === SwapMode.Buy
                ? shift4TokenAmount || cashAmount
                : tokenAmount
            }
            toAmount={estimatedTokenOutAmount}
            token={token}
            displayToken={displayToken}
            mining={!!transactionHash}
          />
        )}
        {transactionState === ProcessingState.DepositSuccess && (
          <SuccessView
            title={"Deposit successful"}
            amount={shift4TokenAmount}
            token={cashToken}
            button={
              <Button
                size={ButtonSizes.Large}
                disabled={isFetchingRoute}
                onClick={() => {
                  executeSwapTransaction();
                }}
              >
                {isFetchingRoute ? (
                  <div className="flex justify-center items-center gap-2">
                    <div>Loading</div>
                    <Spinner className="w-4 h-4 fill-white" />
                  </div>
                ) : (
                  <>{swapMode}</>
                )}
              </Button>
            }
            footer={
              token && (
                <div className="flex flex-col gap-1 w-full">
                  <div className="text-md text-text_primary text-center">
                    {"Use it to buy:"}
                  </div>
                  <div className="flex rounded-[12px] bg-theme_color bg-opacity-20">
                    <div className="flex items-center justify-center gap-1 p-3 w-full">
                      {isFetchingRoute ? (
                        <Skeleton className="w-[84px] h-[26px]" />
                      ) : (
                        <div className="font-bold text-text_primary text-2xl">
                          {estimatedTokenOutAmount}
                        </div>
                      )}

                      <TokenLogo
                        token={displayToken || token}
                        tokenLogoClassName="w-5 min-w-5 h-5"
                        unknownTokenLogoClassName="w-5 min-w-5 h-5 text-sm"
                        chain={chain}
                      />
                      <div className="font-medium text-text_primary text-2xl text-opacity-80">
                        {displayToken?.symbol || token?.symbol}
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
          />
        )}
        {transactionState === ProcessingState.DepositError && (
          <ErrorView title={"Deposit unsuccessful"} />
        )}
        {transactionState === ProcessingState.TransactionSuccess && (
          <SuccessView
            title={"Transaction successful"}
            amount={tokenOutAmount}
            cashbackAmount={cashbackAmount}
            pointsAmount={pointsAmount}
            token={swapMode === SwapMode.Buy ? token : cashToken}
            displayToken={swapMode === SwapMode.Buy ? displayToken : undefined}
            fromBuyOrSell
          />
        )}
        {transactionState === ProcessingState.TransactionError && (
          <ErrorView
            title={"Transaction unsuccessful"}
            description={transactionError}
          />
        )}
        {transactionState === ProcessingState.ChainlinkSwapFailed && (
          <ChainlinkSwapFailedView token={token} amount={shift4TokenAmount} />
        )}
      </>
    </PageLayout>
  );
};
